import { FC } from 'react';
import styled from 'styled-components';

import { Skeleton } from 'ui/skeleton';
import { colors, spacing } from 'ui/theme';
import { layoutLRPadding, layoutTopPadding } from 'ui/theme/spacing';
import { pxToRem } from 'utils/convert';
import { useUserMetadataQueryContext } from './contexts/UserMetadataQueryContext';

import { Header } from './header';
import { UserProfilesSideNav } from './sidenav';
import { UserProfilesSideNavSkeleton } from './sidenav/UserProfilesSideNavSkeleton';

const UserProfilesPageGrid = styled.div`
  display: grid;
  grid-template-columns: min-content minmax(0, 1fr);
  grid-template-rows: 1fr;
  grid-template-areas: 'header header' 'sideNav content';
  margin: -${layoutTopPadding} -${layoutLRPadding} calc(-${spacing.base} - 1px) -${layoutLRPadding};
  background-color: ${colors.grey1};
  border-bottom: ${spacing.single} solid ${colors.grey25};
`;

const UserProfilesHeaderGridArea = styled.div`
  grid-area: header;
`;

const UserProfilesSideNavGridArea = styled.div`
  grid-area: sideNav;
  margin-left: ${layoutLRPadding};
  padding: ${pxToRem(38)} 0;
`;

const UserProfilesContentGridArea = styled.div`
  grid-area: content;
  margin-right: ${layoutLRPadding};
  padding: ${pxToRem(38)} 0;
`;

const ContentLoadingSkeleton = styled(Skeleton)`
  height: ${spacing.x12};
  background-color: ${colors.grey5};
  margin: ${spacing.base} ${layoutLRPadding} ${spacing.base} 0;
`;

type ProfileLayout = {
  'data-test'?: string;
};

export const ProfileLayout: FC<React.PropsWithChildren<ProfileLayout>> = ({
  children,
  ...props
}) => {
  const { isLoading, isError } = useUserMetadataQueryContext();
  return (
    <UserProfilesPageGrid data-test={props['data-test']}>
      <UserProfilesHeaderGridArea>
        <Header />
      </UserProfilesHeaderGridArea>
      <UserProfilesSideNavGridArea>
        {isLoading ? <UserProfilesSideNavSkeleton /> : !isError && <UserProfilesSideNav />}
      </UserProfilesSideNavGridArea>
      <UserProfilesContentGridArea>
        {isLoading ? (
          <ContentLoadingSkeleton data-test="user-profile-content-skeleton" />
        ) : (
          !isError && children
        )}
      </UserProfilesContentGridArea>
    </UserProfilesPageGrid>
  );
};
